/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useRef, useEffect, useState } from 'react';

import { parsePreview, PreviewReport, CustomizablePapaParseConfig } from '../../parser';
import { ImporterFrame } from '../ImporterFrame';
import { FileSelector } from './FileSelector';
import { FormatDataRowPreview } from './FormatDataRowPreview';
import { FormatErrorMessage } from './FormatErrorMessage';

import './FileStep.scss';
import { useLocale } from '../../locale/LocaleContext';

export interface FileStepState extends PreviewReport {
  papaParseConfig: CustomizablePapaParseConfig; // config that was used for preview parsing
  hasHeaders: boolean;
  stepperComponent?: any;
  triggerUnAcceptFile?: any;
}

export const FileStep: React.FC<{
  customConfig: CustomizablePapaParseConfig;
  defaultNoHeader?: boolean;
  prevState: FileStepState | null;
  onChange: (state: FileStepState | null) => void;
  onAccept: () => void;
  onBack?: () => void;
  stepperComponent?: any;
  triggerUnAcceptFile?: any;
}> = ({
  customConfig,
  defaultNoHeader,
  prevState,
  onChange,
  onAccept,
  onBack,
  stepperComponent,
  triggerUnAcceptFile,
}) => {
  const l10n = useLocale('fileStep');

  // seed from previous state as needed
  const [selectedFile, setSelectedFile] = useState<File | null>(prevState ? prevState.file : null);

  const [preview, setPreview] = useState<any | null>(
    () =>
      prevState && {
        parseError: undefined,
        ...prevState,
      },
  );

  useEffect(() => {
    // anything that is set when file is selected needs to be reset
    setSelectedFile(null);
  }, [triggerUnAcceptFile]);

  const [papaParseConfig, setPapaParseConfig] = useState(prevState ? prevState.papaParseConfig : customConfig);

  const [hasHeaders, setHasHeaders] = useState(prevState ? prevState.hasHeaders : false);

  // wrap in ref to avoid triggering effect
  const customConfigRef = useRef(customConfig);
  customConfigRef.current = customConfig;
  const defaultNoHeaderRef = useRef(defaultNoHeader);
  defaultNoHeaderRef.current = defaultNoHeader;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // notify of current state
  useEffect(() => {
    onChangeRef.current(preview && !preview.parseError ? { ...preview, papaParseConfig, hasHeaders } : null);
  }, [preview, papaParseConfig, hasHeaders]);

  // perform async preview parse once for the given file
  const asyncLockRef = useRef<number>(0);
  useEffect(() => {
    // clear other state when file selector is reset
    if (!selectedFile) {
      setPreview(null);
      return;
    }

    // preserve existing state when parsing for this file is already complete
    if (preview && preview.file === selectedFile) {
      return;
    }

    const oplock = asyncLockRef.current;

    // lock in the current PapaParse config instance for use in multiple spots
    const config = customConfigRef.current;

    // kick off the preview parse
    parsePreview(selectedFile, config).then((results) => {
      // ignore if stale
      if (oplock !== asyncLockRef.current) {
        return;
      }

      // save the results and the original config
      setPreview(results);
      setPapaParseConfig(config);

      // pre-fill headers flag (only possible with >1 lines)
      setHasHeaders(results.parseError ? false : !defaultNoHeaderRef.current && !(results as any).isSingleLine);
    });

    return () => {
      // invalidate current oplock on change or unmount
      asyncLockRef.current += 1;
    };
  }, [selectedFile, preview]);

  // clear selected file
  // preview result content to display
  const reportBlock = useMemo(() => {
    if (!preview) {
      return null;
    }

    if (preview.parseError) {
      return (
        <div className="CSVImporter_FileStep__mainResultBlock">
          <FormatErrorMessage onCancelClick={() => setSelectedFile(null)}>
            {l10n.getImportError(preview.parseError.message || String(preview.parseError))}
          </FormatErrorMessage>
        </div>
      );
    }

    return (
      <div className="CSVImporter_FileStep__mainResultBlock">
        {preview.parseWarning ? null : (
          <>
            <div className="CSVImporter_FileStep__header">
              {l10n.previewImportHeading}
              {!preview.isSingleLine && ( // hide setting if only one line anyway
                <label className="CSVImporter_FileStep__headerToggle">
                  <input
                    type="checkbox"
                    checked={hasHeaders}
                    onChange={() => {
                      setHasHeaders((prev) => !prev);
                    }}
                  />
                  <span>{l10n.dataHasHeadersCheckbox}</span>
                </label>
              )}
            </div>
            <FormatDataRowPreview hasHeaders={hasHeaders} rows={preview.firstRows} />
          </>
        )}
      </div>
    );
  }, [preview, hasHeaders, l10n]);

  if (!selectedFile) {
    return (
      <>
        <ImporterFrame
          fileName={null}
          nextDisabled={true}
          // TODO all of this onNext logic should probaby be in the activeStep if statements in Importer.tsx except those variables don't exist in that file's function scope
          onNext={() => {
            if (!preview || preview.parseError) {
              throw new Error('unexpected missing preview info');
            }

            onAccept();
          }}
          onCancel={() => {
            setSelectedFile(null);
            onBack();
          }}
          nextLabel={l10n.nextButton}
        >
          {stepperComponent}
          <FileSelector onSelected={(file) => setSelectedFile(file)} />
          {/* {reportBlock || <div className="CSVImporter_FileStep__mainPendingBlock">{l10n.previewLoadingStatus}</div>} */}
        </ImporterFrame>
      </>
    );
  }

  return (
    <ImporterFrame
      fileName={selectedFile.name}
      nextDisabled={!preview || !!preview.parseError || !!preview.parseWarning}
      // TODO all of this onNext logic should probaby be in the activeStep if statements in Importer.tsx
      onNext={() => {
        if (!preview || preview.parseError) {
          throw new Error('unexpected missing preview info');
        }

        onAccept();
      }}
      onCancel={() => {
        setSelectedFile(null);
        onBack();
      }}
      nextLabel={l10n.nextButton}
    >
      {stepperComponent}
      {reportBlock || <div className="CSVImporter_FileStep__mainPendingBlock">{l10n.previewLoadingStatus}</div>}
    </ImporterFrame>
  );
};
